/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Navigate, useParams, useSearchParams } from 'react-router';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';
import { SYSTEM_OVERVIEW_ANOMALY_CONFIGS } from '@/config/kpi/overviewAnomalyConfig';

/* Import components */
import AnomalyMetricsOverview, {
    type AnomalyFilterOption,
} from '@/components/tabs/overview/metrics/AnomalyMetricsOverview';

const SYSTEM_DETAIL_ANOMALY_FILTER_OPTIONS: readonly AnomalyFilterOption[] = [
    { anomalies: ['pv'], label: 'Photovoltaics', value: 'pv' },
    { anomalies: ['bl', 'bl_night'], label: 'Electricity', value: 'bl' },
    { anomalies: ['heating_energy'], label: 'Heat', value: 'heating_energy' },
    { anomalies: ['water_bl'], label: 'Water', value: 'water_bl' },
    { anomalies: ['gas_bl'], label: 'Gas', showWhenEmpty: true, value: 'gas_bl' },
];

function SystemMetricsDetailTab() {
    const { systemKey } = useParams<{ systemKey: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedAnomaly = searchParams.get('anomaly') ?? '';

    if (!systemKey) {
        return <Navigate to={APP_ROUTES.systems} replace />;
    }

    return (
        <AnomalyMetricsOverview
            anomalyFilterOptions={SYSTEM_DETAIL_ANOMALY_FILTER_OPTIONS}
            anomalyConfigs={SYSTEM_OVERVIEW_ANOMALY_CONFIGS}
            description="Overview of all available anomaly metrics."
            fixedSystem={systemKey}
            heading={systemKey}
            idPrefix="system-detail"
            includeDisabledSensors
            selectedAnomaly={selectedAnomaly}
            onSelectedAnomalyChange={(anomaly) => {
                setSearchParams((currentSearchParams) => {
                    const nextSearchParams = new URLSearchParams(currentSearchParams);

                    if (anomaly) {
                        nextSearchParams.set('anomaly', anomaly);
                    } else {
                        nextSearchParams.delete('anomaly');
                    }

                    return nextSearchParams;
                }, { replace: true });
            }}
        />
    );
}

export default SystemMetricsDetailTab;
